import React, { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import classNames from "classnames";
import { z } from "zod";
import {
    Entity,
    EntityProfileIndustry,
    EntityProfileType,
} from "@common/types/entity";
import { Organization } from "@common/types/organization";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import { COUNTRIES } from "@common/countries";
import {
    useCreateOrganizationForOnboardingMutation,
    useOrganization,
    useUpdateOrganizationMutation,
} from "@api/organization.api";
import { useCreateFirmWorkspaceMutation } from "@api/organizationClient.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { EnterKeyHint } from "@components/onboarding/components/EnterKeyHint";
import { VStack } from "@design-system/VStack/VStack";
import animations from "../layout/OnboardingLayout.module.scss";
import { FormikField } from "@components/forms/FormikField/FormikField";
import fieldSpacing from "../components/fieldSpacing.module.scss";
import {
    DEFAULT_COUNTRY_CODE,
    entityTypeItems,
    industryPickerItems,
    isEntityProfileType,
} from "../constants";
import {
    defaultDemoBookkeepingStartDate,
    bookkeepingStartDateAsValue,
} from "../organizationOnboardingForms";
import { MultiSelectPickerField } from "../components/MultiSelectPickerField";
import { type FirmInfoValues } from "./FirmInfo";
import commonStyles from "./common.module.scss";

interface FirmSetupProps {
    entity: Entity;
    firmInfo: FirmInfoValues;
    initialValues: FirmSetupValues | null;
    back(values: FirmSetupValues): void;
    next(organization: Organization): void;
}

const clientCountryItems = COUNTRIES.map(({ code, name }) => ({
    value: code,
    label: name,
}));

const firmSetupFormSchema = z.object({
    legalType: z.nativeEnum(EntityProfileType, {
        required_error: "Entity type is required",
    }),
    clientCountries: z
        .array(z.string())
        .min(1, "At least one country is required"),
    industries: z.array(z.nativeEnum(EntityProfileIndustry)).optional(),
});

export type FirmSetupValues = z.infer<typeof firmSetupFormSchema>;

export const FirmSetup: React.FC<FirmSetupProps> = ({
    entity,
    firmInfo,
    initialValues,
    back,
    next,
}) => {
    const [loading, setLoading] = useState(false);
    const { data: organization } = useOrganization();
    const updateOrganizationMutation = useUpdateOrganizationMutation();
    const createOrganizationMutation =
        useCreateOrganizationForOnboardingMutation();
    const createFirmWorkspaceMutation = useCreateFirmWorkspaceMutation();

    const formik = useFormik<FirmSetupValues>({
        initialValues: initialValues ?? {
            legalType: entity.legalType ?? EntityProfileType.NO_LEGAL_ENTITY,
            clientCountries: organization?.clientCountries ?? [
                DEFAULT_COUNTRY_CODE,
            ],
            industries: organization?.profile?.industries ?? [],
        },
        validate: validateFormikWithZodSchema(firmSetupFormSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({ legalType, clientCountries, industries }) => {
                const { businessName, firmSize, businessTaxReturns } = firmInfo;
                const profile = {
                    firmSize,
                    businessTaxReturns,
                    servicesOffered:
                        organization?.profile?.servicesOffered ?? [],
                    industries,
                };

                if (organization) {
                    const updatedOrganization =
                        await updateOrganizationMutation.mutateAsync({
                            organizationId: organization.id,
                            payload: {
                                name: businessName,
                                profile,
                                clientCountries,
                            },
                        });

                    return next(updatedOrganization);
                }

                const newOrganization =
                    await createOrganizationMutation.mutateAsync({
                        name: businessName,
                        profile,
                        clientCountries,
                    });

                await createFirmWorkspaceMutation.mutateAsync({
                    organizationId: newOrganization.id,
                    payload: {
                        name: businessName,
                        legalType,
                        bookkeepingStartDate: bookkeepingStartDateAsValue(
                            defaultDemoBookkeepingStartDate,
                        ),
                    },
                });

                next(newOrganization);
            },
        }),
    });

    return (
        <div
            className={classNames(commonStyles.content420, animations.fadeIn1)}
        >
            <header
                className={classNames(
                    commonStyles.titleContainer,
                    animations.fadeIn2,
                )}
            >
                <h1 className={commonStyles.title}>Set up your firm</h1>
            </header>

            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <MultiSelectPickerField
                        name="clientCountries"
                        label="Client countries"
                        items={clientCountryItems}
                        searchable
                        emptyLabel="No matching countries"
                        showErrorMessage
                        triggerClassName="mb-1"
                    />

                    <MultiSelectPickerField
                        name="industries"
                        label="Served industries"
                        items={industryPickerItems}
                        searchable
                        emptyLabel="No matching industries"
                        triggerClassName="mb-1"
                    />

                    <FormikField<EntityProfileType>
                        name="legalType"
                        label="Entity type"
                        control="button"
                        className={fieldSpacing.buttonField}
                    >
                        {({ helpers, field, invalid }) => (
                            <Picker.Select
                                items={entityTypeItems}
                                value={
                                    field.value &&
                                    isEntityProfileType(field.value)
                                        ? field.value
                                        : null
                                }
                                onValueChange={(value) => {
                                    if (value) {
                                        helpers.setValue(value);
                                    }
                                }}
                                placeholder="Select"
                                aria-invalid={invalid}
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="xl"
                                        block
                                    />
                                }
                            />
                        )}
                    </FormikField>

                    <VStack as="footer" align="center" className="mt-4">
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            loading={loading}
                            block
                            size="xl"
                            rightIcon={<EnterKeyHint />}
                        >
                            Next
                        </ButtonWithLoader>
                        <Button
                            variant="secondary"
                            disabled={loading}
                            type="button"
                            onClick={() => back(formik.values)}
                            className="mt-4"
                            size="xl"
                        >
                            Back
                        </Button>
                    </VStack>
                </Form>
            </FormikProvider>
        </div>
    );
};
